import moment from "moment";

import { formatAddressLine } from "../Supply/DeliveryDetailDrawer";

import { getUserCredentials } from "../../../../utils/common";

export const SHIPPING_METHODS = {
  BILLABLE_STAMPS: "billable-stamps",
  GENERATE_LABELS: "generate-labels",
};

export const PICKUP_TYPE_KEYS = {
  FEDEX_PICKUP: "fedex-pickup",
  FEDEX_DROPOFF: "fedex-dropoff",
  LOCAL_COURIER: "local-courier",
};

export const PICKUP_CATEGORIES = {
  FEDEX_PICKUP_LABELS: "fedex_pickup_labels",
  FEDEX_PICKUP_NO_LABELS: "fedex_pickup_no_labels",
  FEDEX_DROPOFF_LABELS: "fedex_dropoff_labels",
  FEDEX_DROPOFF_NO_LABELS: "fedex_dropoff_no_labels",
  LOCAL_COURIER: "local_courier",
};

export const FEDEX_EXPRESS_CARRIER = "FDXE";
export const FEDEX_PICKUP_ADDRESS_TYPE = "SHIPPER";
export const FEDEX_NOTIFICATION_FORMAT = "HTML";
export const FEDEX_NOTIFICATION_LOCALE = "en_US";
export const FEDEX_TIME_PARSE_FORMATS = ["h:mm A", "hh:mm A", "HH:mm:ss", "HH:mm"];

export const DEFAULT_WEIGHT_UNIT = "lb";
export const DEFAULT_BOX = {
  totalWeight: undefined,
  weightUnit: DEFAULT_WEIGHT_UNIT,
  dimension1: 10,
  dimension2: 10,
  dimension3: 10,
  quantity: 1,
};

/** Genista Biosciences lab — shipment recipient for generated labels. */
const GENISTA_LAB_RECIPIENT = {
  address: {
    streetLines: ["5500 Hellyer Ave Ste 150"],
    city: "San Jose",
    stateOrProvinceCode: "CA",
    postalCode: "95138",
    countryCode: "US",
    residential: false,
  },
  contact: {
    personName: "SAMPLE RECEIVING",
    emailAddress: "lab@genistabio.com",
    phoneNumber: "4087575594",
    companyName: "GENISTA BIOSCIENCES",
  },
  deliveryInstructions: "",
};

/* -------------------------------------------------------------------------- */
/* Shared formatters                                                          */
/* -------------------------------------------------------------------------- */

/** Used by PaymentStep (currently commented out of the wizard). */
export const formatMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "$0.00";
  return `$${n.toFixed(2)}`;
};

export const getSubmissionId = (submission) => submission?.submit_id ?? "";

export const getSampleCount = (submissions = []) => (
  submissions.reduce((sum, row) => {
    const n = Number(row?.num_samples);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0)
);

export const getTotalBoxes = (boxes = []) => (
  boxes.reduce((sum, box) => {
    const qty = Number(box?.quantity);
    return sum + (Number.isFinite(qty) ? qty : 0);
  }, 0)
);

export const getBoxesSummary = (boxes = []) => {
  const totalQty = getTotalBoxes(boxes) || 1;
  const packageLabel = `${totalQty} Package${totalQty === 1 ? "" : "s"}`;
  const weightLabels = (boxes || []).flatMap((box) => {
    const weight = Number(box?.totalWeight);
    if (!Number.isFinite(weight)) return [];
    const unit = box?.weightUnit || "lb";
    const qty = Number(box?.quantity);
    const count = Number.isFinite(qty) && qty > 0 ? qty : 0;
    const label = `${weight} ${unit}`;
    return Array.from({ length: count }, () => label);
  });

  return weightLabels.length
    ? `${packageLabel}: ${weightLabels.join(", ")}`
    : packageLabel;
};

/** Used by PaymentStep (currently commented out of the wizard). */
export const getListedTotal = (boxes = []) => (
  boxes.reduce((sum, box) => {
    const price = Number(box?.price);
    const qty = Number(box?.quantity);
    const line = (Number.isFinite(price) ? price : 0) * (Number.isFinite(qty) ? qty : 0);
    return sum + line;
  }, 0)
);

export const formatPickupDateDisplay = (pickupDate, format = "dddd, MMMM D") => {
  if (!pickupDate) return "—";
  const date = moment(pickupDate, ["YYYY-MM-DD", "MM/DD/YYYY", "MM/DD/YY"], true);
  const fallback = moment(pickupDate);
  const resolved = date.isValid() ? date : fallback;
  if (!resolved.isValid()) return String(pickupDate);
  return resolved.format(format);
};

export const getPickupSummaryLabel = (pickupDate, earliest, latest) => {
  if (!pickupDate || !earliest || !latest) return "";
  const date = moment(pickupDate, ["YYYY-MM-DD", "MM/DD/YYYY", "MM/DD/YY"], true);
  if (!date.isValid()) return `${earliest} - ${latest}`;
  const today = moment().startOf("day");
  const diff = date.clone().startOf("day").diff(today, "days");
  let dayLabel = date.format("MMM D");
  if (diff === 0) dayLabel = "today";
  if (diff === 1) dayLabel = "tomorrow";
  return `Pickup ${dayLabel} · ${earliest} - ${latest}`;
};

export const buildTrackingRows = (boxes = [], initialTrackingIds = []) => {
  const rows = [];
  let packageIndex = 0;
  (boxes || []).forEach((box, boxIndex) => {
    const quantity = Math.max(1, Number(box?.quantity) || 1);
    for (let i = 0; i < quantity; i += 1) {
      const labelSuffix = quantity > 1 ? ` (${i + 1}/${quantity})` : "";
      rows.push({
        boxIndex: packageIndex,
        sourceBoxIndex: boxIndex,
        label: `Box ${boxIndex + 1}${labelSuffix} · ${box?.totalWeight ?? ""} ${box?.weightUnit || "lb"}`.trim(),
        trackingId: initialTrackingIds[packageIndex]?.trackingId || "",
      });
      packageIndex += 1;
    }
  });
  return rows;
};

/* -------------------------------------------------------------------------- */
/* FedEx time / address helpers                                               */
/* -------------------------------------------------------------------------- */

export const parsePickupTime = (time) => moment(time, FEDEX_TIME_PARSE_FORMATS, true);

export const formatFedexTimeDisplay = (time) => {
  const parsed = parsePickupTime(time);
  return parsed.isValid() ? parsed.format("h:mm A") : time;
};

export const buildHourlyTimeOptions = () => {
  const times = [];
  for (let hour = 0; hour < 24; hour += 1) {
    times.push(moment({ hour, minute: 0 }).format("h:mm A"));
  }
  return times;
};

export const HOURLY_TIME_OPTIONS = buildHourlyTimeOptions();

/** Default window for Local Courier / FedEx Drop-off when times are unset. */
export const DEFAULT_EARLIEST_POSSIBLE_TIME = "8:00 AM";
export const DEFAULT_LATEST_POSSIBLE_TIME = "5:00 PM";

export const isFedexPickupType = (pickupTypeKey) => (
  pickupTypeKey === PICKUP_TYPE_KEYS.FEDEX_PICKUP || !pickupTypeKey
);

export const isLocalCourierType = (pickupTypeKey) => (
  pickupTypeKey === PICKUP_TYPE_KEYS.LOCAL_COURIER
);

/** Same-day local courier cut-off is 7:00 AM; at/after that, earliest pickup is tomorrow. */
export const getLocalCourierEarliestPickupDate = (format = "YYYY-MM-DD") => {
  const now = moment();
  const cutoff = now.clone().startOf("day").hour(7).minute(0)
    .second(0)
    .millisecond(0);
  if (now.isSameOrAfter(cutoff)) {
    return now.clone().add(1, "day").format(format);
  }
  return now.format(format);
};

export const getAccessMinutes = (accessTime) => {
  if (!accessTime) return 0;
  return (Number(accessTime.hours) || 0) * 60 + (Number(accessTime.minutes) || 0);
};

export const toFedexCountryCode = (country) => {
  if (!country) return country;
  if (country.length === 2) return country.toUpperCase();
  if (String(country).toLowerCase() === "united states") return "US";
  return country;
};

export const formatShipDatestamp = (pickupDate) => {
  const parsed = moment(pickupDate, ["YYYY-MM-DD", "MM/DD/YYYY", "MM/DD/YY"], true);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : (pickupDate || "");
};

export const buildFedexReadyTimestamp = (pickupDate, earliestTime) => {
  const dateKey = formatShipDatestamp(pickupDate);
  const readyDateTime = moment(`${dateKey} ${earliestTime}`, "YYYY-MM-DD h:mm A", true);
  if (readyDateTime.isValid()) return readyDateTime.format("YYYY-MM-DDTHH:mm:ss[Z]");
  const fallback = moment(
    `${dateKey} ${earliestTime}`,
    ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm"],
    true,
  );
  return fallback.isValid() ? fallback.format("YYYY-MM-DDTHH:mm:ss[Z]") : undefined;
};

export const buildFedexCloseTime = (latestTime) => {
  const closeTime = parsePickupTime(latestTime);
  return closeTime.isValid() ? closeTime.format("HH:mm:ss") : undefined;
};

/* -------------------------------------------------------------------------- */
/* FedEx availability                                                         */
/* -------------------------------------------------------------------------- */

export const buildFedexAvailabilityPayload = (address, carrierCode = FEDEX_EXPRESS_CARRIER) => {
  const countryCode = toFedexCountryCode(address.country);
  return {
    pickupAddress: {
      streetLines: [address.address_1, address.address_2].filter(Boolean),
      city: address.city,
      stateOrProvinceCode: address.state,
      postalCode: address.zipCode,
      countryCode,
    },
    pickupRequestType: ["SAME_DAY", "FUTURE_DAY"],
    carriers: [carrierCode],
    countryRelationship: countryCode === "US" ? "DOMESTIC" : "INTERNATIONAL",
  };
};

export const extractAvailabilityOptions = (response) => (
  response?.result?.options || []
);

export const filterAvailableOptions = (options = [], carrierCode = FEDEX_EXPRESS_CARRIER) => (
  options.filter((opt) => (
    opt?.available !== false
    && opt?.pickupDate
    && (!opt.carrier || opt.carrier === carrierCode)
  ))
);

export const getOptionDateKey = (pickupDate) => (
  moment(pickupDate, ["YYYY-MM-DD", "MM/DD/YYYY", "MM/DD/YY"], true).format("YYYY-MM-DD")
);

export const mapAvailabilityToDateOptions = (
  options = [],
  carrierCode = FEDEX_EXPRESS_CARRIER,
  {
    valueFormat = "YYYY-MM-DD",
    labelFormat = "MM/DD/YY",
  } = {},
) => {
  const seen = new Set();
  return filterAvailableOptions(options, carrierCode)
    .map((opt) => {
      const value = moment(opt.pickupDate, ["YYYY-MM-DD", "MM/DD/YYYY", "MM/DD/YY"], true);
      if (!value.isValid()) return null;
      const key = value.format(valueFormat);
      if (seen.has(key)) return null;
      seen.add(key);
      return {
        value: key,
        label: value.format(labelFormat),
      };
    })
    .filter(Boolean)
    .sort((a, b) => moment(a.value, valueFormat).diff(moment(b.value, valueFormat)));
};

export const findAvailabilityForDate = (
  options = [],
  pickupDateValue,
  carrierCode = FEDEX_EXPRESS_CARRIER,
) => {
  if (!pickupDateValue) return null;
  const dateKey = moment(
    pickupDateValue,
    ["YYYY-MM-DD", "MM/DD/YYYY", "MM/DD/YY"],
    true,
  ).format("YYYY-MM-DD");
  if (!moment(dateKey, "YYYY-MM-DD", true).isValid()) return null;
  return filterAvailableOptions(options, carrierCode).find((opt) => (
    getOptionDateKey(opt.pickupDate) === dateKey
  )) || null;
};

export const filterLatestTimesByEarliest = (latestTimes = [], earliest, accessMinutes = 0) => {
  if (!earliest) return latestTimes;
  const minLatest = parsePickupTime(earliest).clone().add(accessMinutes, "minutes");
  if (!minLatest.isValid()) return latestTimes;
  return latestTimes.filter((time) => {
    const parsed = parsePickupTime(time);
    return parsed.isValid() && parsed.isSameOrAfter(minLatest);
  });
};

export const getAvailabilityTimeState = (
  options = [],
  pickupDateValue,
  carrierCode = FEDEX_EXPRESS_CARRIER,
) => {
  const match = findAvailabilityForDate(options, pickupDateValue, carrierCode);
  if (!match) {
    return {
      readyTimes: [],
      latestTimes: [],
      defaultReady: undefined,
      defaultLatest: undefined,
      accessMinutes: 0,
      carrier: undefined,
      countryRelationship: undefined,
      scheduleDay: undefined,
    };
  }
  return {
    readyTimes: (match.readyTimeOptions || []).map(formatFedexTimeDisplay),
    latestTimes: (match.latestTimeOptions || []).map(formatFedexTimeDisplay),
    defaultReady: match.defaultReadyTime
      ? formatFedexTimeDisplay(match.defaultReadyTime)
      : undefined,
    defaultLatest: match.defaultLatestTimeOptions
      ? formatFedexTimeDisplay(match.defaultLatestTimeOptions)
      : undefined,
    accessMinutes: getAccessMinutes(match.accessTime),
    carrier: match.carrier,
    countryRelationship: match.countryRelationship,
    scheduleDay: match.scheduleDay,
  };
};

/* -------------------------------------------------------------------------- */
/* Pickup request payload builders                                            */
/* -------------------------------------------------------------------------- */

const toWeightUnit = (unit) => (
  String(unit || "lb").toLowerCase() === "kg" ? "KG" : "LB"
);

const toWeightValue = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const expandPackageUnits = (boxes = []) => {
  const units = [];
  (boxes || []).forEach((box) => {
    const quantity = Math.max(1, Number(box?.quantity) || 1);
    for (let i = 0; i < quantity; i += 1) {
      units.push({
        totalWeight: toWeightValue(box?.totalWeight),
        weightUnit: toWeightUnit(box?.weightUnit),
      });
    }
  });
  return units;
};

const sumTotalWeight = (packageUnits = []) => (
  packageUnits.reduce((sum, unit) => sum + toWeightValue(unit.totalWeight), 0)
);

/**
 * Resolve pickup_category from pickup type + shipping method.
 */
export const resolvePickupCategory = (pickupTypeKey, shippingMethod) => {
  if (pickupTypeKey === PICKUP_TYPE_KEYS.LOCAL_COURIER) {
    return PICKUP_CATEGORIES.LOCAL_COURIER;
  }
  if (pickupTypeKey === PICKUP_TYPE_KEYS.FEDEX_DROPOFF) {
    return shippingMethod === SHIPPING_METHODS.GENERATE_LABELS
      ? PICKUP_CATEGORIES.FEDEX_DROPOFF_LABELS
      : PICKUP_CATEGORIES.FEDEX_DROPOFF_NO_LABELS;
  }
  return shippingMethod === SHIPPING_METHODS.GENERATE_LABELS
    ? PICKUP_CATEGORIES.FEDEX_PICKUP_LABELS
    : PICKUP_CATEGORIES.FEDEX_PICKUP_NO_LABELS;
};

const buildFedexPartyFromAddress = (address, companyName) => ({
  address: {
    streetLines: [address?.address_1, address?.address_2].filter(Boolean),
    city: address?.city || "",
    stateOrProvinceCode: address?.state || "",
    postalCode: address?.zipCode || "",
    countryCode: toFedexCountryCode(address?.country) || "US",
    residential: false,
  },
  contact: {
    personName: address?.name || "",
    emailAddress: address?.email || "",
    phoneExtension: "",
    phoneNumber: address?.phone || "",
    companyName: companyName || "",
  },
});

const buildShipmentPayload = ({
  schedule,
  packageUnits,
  companyName,
  deliveryInstructions = "",
  omitShipDatestamp = false,
}) => {
  const pickupAddress = schedule?.pickupAddress || {};
  const totalWeight = Number(sumTotalWeight(packageUnits).toFixed(1));

  return {
    mergeLabelDocOption: "LABELS_AND_DOCS",
    requestedShipment: {
      ...(omitShipDatestamp ? {} : {
        shipDatestamp: formatShipDatestamp(schedule?.pickupDate),
      }),
      shipper: buildFedexPartyFromAddress(pickupAddress, companyName),
      recipients: [{
        ...GENISTA_LAB_RECIPIENT,
        deliveryInstructions: deliveryInstructions || "",
      }],
      pickupType: "USE_SCHEDULED_PICKUP",
      serviceType: "PRIORITY_OVERNIGHT",
      packagingType: "YOUR_PACKAGING",
      totalWeight,
      shippingChargesPayment: {
        paymentType: "RECIPIENT",
      },
      blockInsightVisibility: true,
      labelSpecification: {
        labelFormatType: "COMMON2D",
        labelOrder: "SHIPPING_LABEL_FIRST",
        labelStockType: "PAPER_LETTER",
        imageType: "PDF",
      },
      preferredCurrency: "USD",
      totalPackageCount: packageUnits.length,
      requestedPackageLineItems: packageUnits.map((unit) => ({
        weight: {
          units: unit.weightUnit,
          value: toWeightValue(unit.totalWeight) || 0.1,
        },
      })),
    },
    labelResponseOptions: "URL_ONLY",
    shipAction: "CONFIRM",
    processingOptionType: "SYNCHRONOUS_ONLY",
  };
};

const buildPickupPayload = ({
  schedule,
  packaging,
  packageUnits,
  companyName,
  trackingIds = [],
}) => {
  const pickupAddress = schedule?.pickupAddress || {};
  const instructions = packaging?.instructions || "";
  const totalWeightValue = Number(sumTotalWeight(packageUnits).toFixed(1));
  const primaryUnit = packageUnits[0]?.weightUnit || "LB";
  const firstTracking = trackingIds
    .map((row) => String(row?.trackingId || "").trim())
    .find(Boolean) || "";

  return {
    originDetail: {
      pickupAddressType: FEDEX_PICKUP_ADDRESS_TYPE,
      pickupLocation: {
        contact: {
          companyName: companyName || "",
          personName: pickupAddress.name || "",
          phoneNumber: pickupAddress.phone || "",
        },
        address: {
          streetLines: [pickupAddress.address_1, pickupAddress.address_2].filter(Boolean),
          city: pickupAddress.city || "",
          stateOrProvinceCode: pickupAddress.state || "",
          postalCode: pickupAddress.zipCode || "",
          countryCode: toFedexCountryCode(pickupAddress.country) || "US",
        },
        deliveryInstructions: instructions,
      },
      readyDateTimestamp: buildFedexReadyTimestamp(
        schedule?.pickupDate,
        schedule?.earliestPossibleTime,
      ),
      customerCloseTime: buildFedexCloseTime(schedule?.latestPossibleTime),
    },
    totalWeight: {
      units: primaryUnit,
      value: totalWeightValue,
    },
    packageCount: packageUnits.length,
    carrierCode: FEDEX_EXPRESS_CARRIER,
    trackingNumber: firstTracking,
    pickupNotificationDetail: {
      emailDetails: pickupAddress.email
        ? [{
          address: pickupAddress.email,
          locale: FEDEX_NOTIFICATION_LOCALE,
        }]
        : [],
      format: FEDEX_NOTIFICATION_FORMAT,
      userMessage: instructions,
    },
  };
};

const buildInternalPayload = ({
  pickupCategory,
  schedule,
  packageUnits,
  trackingIds = [],
  selectedSubmissions = [],
  isBillable,
}) => {
  const userInfo = getUserCredentials() || {};
  const pickupAddress = schedule?.pickupAddress || {};
  const earliestHms = buildFedexCloseTime(schedule?.earliestPossibleTime);
  const latestHms = buildFedexCloseTime(schedule?.latestPossibleTime);
  const totalWeightValue = Number(sumTotalWeight(packageUnits).toFixed(1));
  const primaryUnit = (packageUnits[0]?.weightUnit || "LB").toLowerCase();

  const internal = {
    company_name: userInfo.company_name || "",
    customer_id: userInfo.customer_id || "",
    requested_by: userInfo.uid === "support" ? "" : (userInfo.user_name || ""),
    pickup_date: moment(schedule?.pickupDate).format("YYYY-MM-DD"),
    pickup_window: earliestHms && latestHms ? `${earliestHms}-${latestHms}` : "",
    pickup_category: pickupCategory,
    package_count: packageUnits.length,
    total_weight: `${totalWeightValue} ${primaryUnit}`,
    submission_id_list: (selectedSubmissions || [])
      .map(getSubmissionId)
      .filter(Boolean),
    pickup_details: {
      name: pickupAddress.name || "",
      email: pickupAddress.email || "",
      phone: pickupAddress.phone || "",
      address: formatAddressLine(pickupAddress),
    },
  };

  if (isBillable || pickupCategory === PICKUP_CATEGORIES.LOCAL_COURIER) {
    internal.boxes = packageUnits.map((unit, index) => ({
      weight: unit.totalWeight ? String(unit.totalWeight) : "",
      tracking_id: String(trackingIds[index]?.trackingId || "").trim(),
    }));
  }

  return internal;
};

/**
 * Build POST /api/pickup-request payload:
 * - fedex_pickup_labels:      fedex { shipment + pickup } + internal
 * - fedex_pickup_no_labels:   fedex { pickup } + internal
 * - fedex_dropoff_labels:     fedex { shipment } + internal
 * - fedex_dropoff_no_labels:  internal only
 * - local_courier:            internal only
 */
export const buildPickupRequestPayload = ({
  pickupTypeKey,
  shippingMethod,
  schedule,
  packaging,
  trackingIds = [],
  selectedSubmissions = [],
}) => {
  const pickupCategory = resolvePickupCategory(pickupTypeKey, shippingMethod);
  const packageUnits = expandPackageUnits(packaging?.boxes);
  const userInfo = getUserCredentials() || {};
  const isBillable = shippingMethod === SHIPPING_METHODS.BILLABLE_STAMPS;
  const companyName = userInfo.company_name || "";

  const payload = {
    internal: buildInternalPayload({
      pickupCategory,
      schedule,
      packaging,
      packageUnits,
      trackingIds,
      selectedSubmissions,
      isBillable,
    }),
  };

  if (
    pickupCategory === PICKUP_CATEGORIES.LOCAL_COURIER
    || pickupCategory === PICKUP_CATEGORIES.FEDEX_DROPOFF_NO_LABELS
  ) {
    return payload;
  }

  const fedex = {};

  if (
    pickupCategory === PICKUP_CATEGORIES.FEDEX_PICKUP_LABELS
    || pickupCategory === PICKUP_CATEGORIES.FEDEX_DROPOFF_LABELS
  ) {
    fedex.shipment = buildShipmentPayload({
      schedule,
      packageUnits,
      companyName,
      deliveryInstructions: packaging?.instructions || "",
      omitShipDatestamp: pickupCategory === PICKUP_CATEGORIES.FEDEX_DROPOFF_LABELS,
    });
  }

  if (
    pickupCategory === PICKUP_CATEGORIES.FEDEX_PICKUP_LABELS
    || pickupCategory === PICKUP_CATEGORIES.FEDEX_PICKUP_NO_LABELS
  ) {
    fedex.pickup = buildPickupPayload({
      schedule,
      packaging,
      packageUnits,
      companyName,
      trackingIds,
    });
  }

  payload.fedex = fedex;
  return payload;
};
